
import React from 'react';
import { TableRow, TableCell } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { MoreVertical, Edit, Trash2, Power, Save, X } from 'lucide-react';
import { Device, DeviceEditValues } from './DeviceTypes';
import { getStatusBadge, getDepartmentBadge, getTypeIcon } from './DeviceBadges';

interface DeviceTableRowProps {
  device: Device;
  editingDevice: string | null;
  editValues: DeviceEditValues;
  onEditDevice: (device: Device) => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onDeleteDevice: (deviceId: string) => void;
  onToggleStatus: (deviceId: string, currentStatus: string) => void;
  onInputChange: (field: keyof Device, value: string) => void;
  formatDate: (dateString: string) => string;
}

const DeviceTableRow = ({
  device,
  editingDevice,
  editValues,
  onEditDevice,
  onSaveEdit,
  onCancelEdit,
  onDeleteDevice,
  onToggleStatus,
  onInputChange,
  formatDate
}: DeviceTableRowProps) => {
  const isEditing = editingDevice === device.id;
  
  return (
    <TableRow key={device.id}>
      <TableCell className="font-mono text-sm">
        {device.id}
      </TableCell>
      <TableCell>
        {isEditing ? (
          <Input 
            value={editValues.name || ''} 
            onChange={(e) => onInputChange('name', e.target.value)}
            className="w-full"
          />
        ) : (
          <div className="flex items-center space-x-2">
            <span>{getTypeIcon(device.type)}</span>
            <span>{device.name}</span>
          </div>
        )}
      </TableCell>
      <TableCell>
        {isEditing ? (
          <Input 
            value={editValues.department || ''} 
            onChange={(e) => onInputChange('department', e.target.value)}
            className="w-full"
          />
        ) : (
          getDepartmentBadge(device.department)
        )}
      </TableCell>
      <TableCell>
        {isEditing ? (
          <Input 
            value={editValues.category || ''} 
            onChange={(e) => onInputChange('category', e.target.value)}
            className="w-full"
          />
        ) : (
          device.category
        )}
      </TableCell>
      <TableCell>
        {isEditing ? (
          <Input 
            value={editValues.serial || ''} 
            onChange={(e) => onInputChange('serial', e.target.value)}
            className="w-full"
          />
        ) : (
          device.serial
        )}
      </TableCell>
      <TableCell>{getStatusBadge(device.status)}</TableCell>
      <TableCell>{formatDate(device.lastActive)}</TableCell>
      <TableCell>
        {isEditing ? (
          <div className="flex space-x-1">
            <Button variant="ghost" size="sm" onClick={onSaveEdit}>
              <Save className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={onCancelEdit}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEditDevice(device)}>
                <Edit className="mr-2 h-4 w-4" />
                <span>Edit</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onToggleStatus(device.id, device.status)}>
                <Power className="mr-2 h-4 w-4" />
                <span>{device.status === 'active' ? 'Deactivate' : 'Activate'}</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onDeleteDevice(device.id)}>
                <Trash2 className="mr-2 h-4 w-4" />
                <span>Delete</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </TableCell>
    </TableRow>
  );
};

export default DeviceTableRow;
